<script setup lang="ts">
import { useDateTime } from '@ai/ui-components'
import StopIcon from '@/assets/img/svg/icons/stop_solid.svg?component'

import type { ActiveCall } from 'shared/src'
import { useLanguages } from '@/composables/translations/useLanguages.ts'
import { CallStatusSeverity } from 'shared/src'
import { ActionButton } from '@/components/BusinessLogic/TableActions'
interface Props {
  calls: ActiveCall[]
  agentId: string
  listeningId?: string | null
}

const props = defineProps<Props>()
defineEmits<{
  (e: 'onClickListen', roomName: string): void
  (e: 'onClickDetails', item: ActiveCall): void
}>()

const localCalls = ref([...props.calls])

watch(
  () => props.calls,
  (newVal) => (localCalls.value = [...newVal]),
)
const { t, locale } = useI18n({ useScope: 'global' })
const { formatDateTime, formatTimerDuration } = useDateTime()

const calculateFirstBadgeMinWidth = (cell: HTMLElement) => {
  const primary = cell.querySelector<HTMLElement>('[data-badge-first]')
  if (!primary) return 0
  const wrapper = cell.querySelector<HTMLElement>('[data-badges-wrapper]')
  const gapRaw = wrapper ? getComputedStyle(wrapper).columnGap : '4'
  const gap = Number.isFinite(parseFloat(gapRaw)) ? parseFloat(gapRaw) : 4
  const result = Math.ceil(primary.getBoundingClientRect().width + gap * 7)
  return result
}

const LiveColumns = computed(() => [
  { key: 'callerNumber', label: t('caller_number'), htmlTitle: t('callers_phone_number') },
  { key: 'startTime', label: t('call_start_time'), htmlTitle: t('callers_start_time') },
  {
    key: 'duration',
    label: t('duration'),
    htmlTitle: t('shows_the_duration_of_the_ongoing_call'),
  },
  { key: 'language', label: t('language'), htmlTitle: t('ongoing_conversation_detected_language') },
  {
    key: 'status',
    label: t('call_status'),
    htmlTitle: t('indicates_the_status_virtual_agent_to_provide_answers_for_to_the_recipient'),
    dynamicMinWidthFn: calculateFirstBadgeMinWidth,
  },
  { key: 'id', label: t('call_id'), htmlTitle: t('callers_id') },
])

const { languageLabel } = useLanguages()
</script>

<template>
  <OTable
    id="active_calls"
    :columns="LiveColumns"
    v-model="localCalls"
    sortable
    resizable
    column-resize-mode="indicator"
  >
    <template #startTime="{ item }">
      <OOverflowTooltip
        :content="formatDateTime({ format: 'dateTime', value: item.startTime, fallback: '-' })"
      >
        {{ formatDateTime({ format: 'dateTime', value: item.startTime, fallback: '-' }) }}
      </OOverflowTooltip>
    </template>

    <template #duration="{ item }">
      <OOverflowTooltip
        v-if="item.durationSec !== undefined"
        :content="
          formatTimerDuration({ value: item.durationSec, alwaysIncludeHours: true, fallback: '-' })
        "
      >
        {{
          formatTimerDuration({ value: item.durationSec, alwaysIncludeHours: true, fallback: '-' })
        }}
      </OOverflowTooltip>
    </template>

    <template #status="{ item }">
      <div data-badges-wrapper class="flex gap-1 items-center min-w-0">
        <OBadge
          v-if="item.status.length > 0"
          data-badge-first
          variant="secondary"
          :color="
            CallStatusSeverity[
              item.status[item.status.length - 1] as keyof typeof CallStatusSeverity
            ] > 0
              ? 'warning'
              : 'success'
          "
          :content="t(item.status[item.status.length - 1])"
        />

        <OBadge
          v-if="item.status.length > 1"
          variant="secondary"
          color="warning"
          :content="`+${item.status.length - 1}`"
          truncate
        />

        <span v-if="item.status.length === 0">-</span>
      </div>
    </template>

    <template #language="{ item }">
      <OOverflowTooltip :content="languageLabel(locale, item.language)">
        {{ languageLabel(locale, item.language) }}
      </OOverflowTooltip>
    </template>

    <template #actions="{ item }">
      <ActionButton
        :tooltip="listeningId === item.roomName ? t('stop_listening_call') : t('listen')"
        icon="listen"
        :icon-component="listeningId === item.roomName ? StopIcon : null"
        data-testid="row-action-listen"
        @click="$emit('onClickListen', item.roomName)"
      />

      <ActionButton
        :tooltip="t('view_details')"
        icon="details"
        data-testid="row-action-details"
        @click="$emit('onClickDetails', item)"
      />
    </template>
  </OTable>
</template>
